import {
  useIntegrator,
  type IntegratorWallets,
  type PunterWalletsUpdateData
} from '@nsftx/seven-client-retail-vue-integrator'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useWalletStore } from '@/stores/wallet/walletStore'
import errorParser from '@/utils/errorParser'
import WalletError from './classes/WalletError'

type Resolve<T = void> = (value: T | PromiseLike<T>) => void
type Reject = (err?: unknown) => void

const LOG_PREFIX = '[walletService]'
const GET_WALLETS_TIMEOUT_MS = 5000
let walletUpdateIntervalId: ReturnType<typeof setInterval> | null = null
let isRunning = false
let runningInitiator: null | 'interval' | 'ui' = null
let waitingList: Array<{ initiator: 'interval' | 'ui'; resolve: Resolve; reject: Reject }> = []

const isWalletResponseSuccess = (response: unknown): response is PunterWalletsUpdateData => {
  return typeof response === 'object' && response !== null && 'wallets' in response
}

const fetchWallets = async () => {
  const { setWallets } = useWalletStore()
  const { getWallets: getWalletsFromIntegrator } = useIntegrator()
  const response = await getWalletsFromIntegrator({ timeout: GET_WALLETS_TIMEOUT_MS })

  if (isWalletResponseSuccess(response)) {
    logService.info(`${LOG_PREFIX} Getting wallets for the punter was successful.`, {
      code: 'ST_GET_WALLETS_RESPONSE_SUCCESS',
      details: {
        wallets: response.wallets
      }
    })
    setWallets(response.wallets as unknown as IntegratorWallets)
    return
  }

  const errMessage = 'Received error response from getWallets'
  const code = 'ST_GET_WALLETS_RESPONSE_ERROR'
  throw new WalletError(errMessage, code, {
    cause: response // this is CustomError class from IntegratorParentSSBT
  })
}

const getWallets = async (initiator: 'ui' | 'interval'): Promise<void> => {
  // Already running — fight for the pending slot
  if (isRunning) {
    // If an interval triggers while another call is running, it must NOT run nor overwrite the UI-triggered request.
    if (initiator === 'interval') {
      if (runningInitiator === 'ui') {
        logService.debug(`${LOG_PREFIX} Ignoring interval since there is UI call running.`, {
          code: 'ST_GET_WALLETS_INTERVAL_IGNORED_DUE_TO_UI_CALL'
        })
        return
      }
      // If both interval and UI triggers happen during a running call, only ONE call should run afterward — and UI-triggered takes priority.
      if (waitingList.some((item) => item.initiator === 'ui')) {
        logService.debug(`${LOG_PREFIX} Ignoring interval since there are UI calls on the hold.`, {
          code: 'ST_GET_WALLETS_INTERVAL_IGNORED_DUE_TO_PENDING_UI_CALLS'
        })
        return
      }
    }

    // Return a promise that will be resolved/rejected when this entry is drained
    return new Promise<void>((resolve, reject) => {
      if (initiator === 'ui') {
        // reject any pending interval calls, since UI takes priority over interval
        for (const item of waitingList) {
          if (item.initiator === 'interval')
            item.reject(
              new WalletError(
                'Interval call rejected since UI has the priority over it.',
                'ST_GET_WALLETS_ABORTED'
              )
            )
        }
        waitingList = waitingList.filter((item) => item.initiator !== 'interval')
      }
      waitingList.push({ initiator, resolve, reject })
    })
  }

  isRunning = true
  runningInitiator = initiator

  try {
    await fetchWallets()
  } finally {
    isRunning = false
    runningInitiator = null

    if (waitingList.length > 0) {
      const next = waitingList.shift()!
      getWallets(next.initiator).then(next.resolve).catch(next.reject)
    }
  }
}

const getInitialWallet = (wallets: IntegratorWallets) => {
  const realWallets = wallets?.filter(({ groupType }) => groupType === 'REAL')
  const positiveRealWallet = realWallets?.find(({ balance }) => Number(balance) > 0)

  return positiveRealWallet ?? realWallets[0] ?? null
}

const startWalletIntervalUpdate = () => {
  const INTERVAL_MS = 20000

  stopWalletIntervalUpdate()

  walletUpdateIntervalId = setInterval(async () => {
    try {
      await getWallets('interval')
    } catch (err) {
      logService.error(
        `${LOG_PREFIX} Failed to get wallets for the punter on wallet interval check.`,
        {
          code: 'ST_GET_WALLETS_FAILED',
          ...errorParser.parseUpstream(err)
        }
      )
    }
  }, INTERVAL_MS)
}

const stopWalletIntervalUpdate = () => {
  if (walletUpdateIntervalId) {
    clearInterval(walletUpdateIntervalId)
  }
}

const initWallets = async () => {
  try {
    await getWallets('ui')
  } catch (err) {
    logService.error(`${LOG_PREFIX} Failed to get wallets for the punter on wallet init.`, {
      code: 'ST_GET_WALLETS_FAILED',
      ...errorParser.parseUpstream(err)
    })
  }

  startWalletIntervalUpdate()
}

export {
  getWallets,
  getInitialWallet,
  initWallets,
  startWalletIntervalUpdate,
  stopWalletIntervalUpdate
}
